/**
 * Builds the German "request from owner" email draft the review step
 * (Step3Review) offers staff for a property owner (COL-529, point 4).
 *
 * It lists ONLY the checklist items the admin flagged as "request from owner"
 * — i.e. documents the admin set to status "requested" with source "owner" in
 * the Checkliste step — grouped by section, one section bullet with the
 * requested items as sub-bullets. Items are aggregated across every apartment
 * of the property (one email per property). Nothing flagged → no draft.
 *
 * Pure + framework-free so the request math and the body assembly are
 * unit-testable without rendering the wizard.
 *
 * The email always goes out in German — it is read by the owner, not by the
 * admin — so the labels are fixed here rather than read through t(). They are
 * kept byte-identical to the German side of `admin.checklist.item.<key>`
 * (20261028210700_i18n_property_wizard_v3.sql), the same labels the owner sees
 * in the checklist, so the two never drift.
 *
 * ponytail: only documents can be flagged request-from-owner (the "Request
 * from owner" control lives on GenericDoc), so `SECTIONS` yields the Dokumente
 * section in practice today. The object/contact rows are kept so the grouping
 * stays correct the day another item type gains the flag.
 */
import {
  DOC_IDS,
  type ChecklistItemKey,
  type ChecklistState,
  type DocId,
} from "./checklist";

/** German labels, mirroring admin.checklist.item.<key> (de column). */
const ITEM_LABELS_DE: Record<ChecklistItemKey, string> = {
  wlan: "WLAN-Zugang",
  strom: "Stromvertrag",
  gas: "Gasvertrag",
  rundfunk: "Rundfunkbeitrag",
  energie: "Energieausweis",
  eigentum: "Eigentumsnachweis",
  grundriss: "Grundriss",
  photos: "Fotos",
  ware: "Inventar / Ausstattung",
  washer: "Waschmaschine",
  smoke: "Rauchmelder",
  key: "Schlüssel",
  bell: "Klingelschild",
  contact: "Ansprechpartner",
  viewing: "Besichtigungskontakt",
};

/** Section groups + German headings, mirroring ChecklistV3's GROUPS. */
const SECTIONS: { labelDe: string; keys: readonly ChecklistItemKey[] }[] = [
  { labelDe: "Dokumente", keys: DOC_IDS },
  {
    labelDe: "Die Wohnung",
    keys: ["photos", "ware", "washer", "smoke", "key", "bell"],
  },
  { labelDe: "Kontakte", keys: ["contact", "viewing"] },
];

/** Just the apartment fields the draft needs. */
export type EmailApartment = { checklist: ChecklistState };

/**
 * An item is "request from owner" when the admin set the document to status
 * "requested" with source "owner" in the Checkliste step. Only documents carry
 * a status/source, so non-doc keys are never requested (see the header).
 */
function isRequestedFromOwner(
  c: ChecklistState,
  key: ChecklistItemKey
): boolean {
  if (!(DOC_IDS as readonly string[]).includes(key)) return false;
  const d = c.docs[key as DocId];
  return d.status === "requested" && d.src === "owner";
}

export type RequestedSection = { labelDe: string; items: string[] };

/**
 * The request-from-owner items, grouped by section, aggregated across every
 * apartment of the property (a section/item appears if ANY apartment requests
 * it). Sections with nothing requested are dropped.
 */
export function requestedSections(
  apartments: EmailApartment[]
): RequestedSection[] {
  return SECTIONS.map((sec) => ({
    labelDe: sec.labelDe,
    items: sec.keys
      .filter((k) =>
        apartments.some((a) => isRequestedFromOwner(a.checklist, k))
      )
      .map((k) => ITEM_LABELS_DE[k]),
  })).filter((s) => s.items.length > 0);
}

export type OwnerEmailDraft = {
  subject: string;
  body: string;
  /** How many items are requested from the owner (0 → nothing to send). */
  count: number;
};

/**
 * The editable German draft: subject + plain-text body listing the
 * request-from-owner items grouped by section. Staff edit To/Subject/Body
 * before submit, so this is a best-guess prefill, not a final message. When
 * nothing is flagged the body is empty and `count` is 0 (the UI disables send).
 */
export function buildOwnerEmailDraft(opts: {
  propertyTitle: string;
  ownerName?: string;
  address?: string;
  apartments: EmailApartment[];
}): OwnerEmailDraft {
  const title = opts.propertyTitle.trim() || "Ihre Immobilie";
  const subject = `Benötigte Unterlagen für ${title}`;
  const sections = requestedSections(opts.apartments);
  const count = sections.reduce((n, s) => n + s.items.length, 0);

  if (count === 0) return { subject, body: "", count: 0 };

  const ownerName = opts.ownerName?.trim();
  const place = opts.address?.trim() ? ` (${opts.address.trim()})` : "";
  const lines = [
    ownerName ? `Guten Tag ${ownerName},` : "Guten Tag,",
    "",
    `bei der Vorbereitung Ihrer Immobilie „${title}“${place} für die Vermietung ist uns aufgefallen, dass noch einige Angaben bzw. Unterlagen fehlen. Nachfolgend finden Sie, was wir noch von Ihnen benötigen:`,
    "",
  ];
  for (const s of sections) {
    lines.push(`• ${s.labelDe}`);
    for (const item of s.items) lines.push(`   – ${item}`);
    lines.push("");
  }
  lines.push(
    "Bitte senden Sie uns die fehlenden Unterlagen zu, damit wir Ihre Immobilie veröffentlichen können.",
    "",
    "Beste Grüße",
    "Ihr coliviq-Team"
  );

  return { subject, body: lines.join("\n"), count };
}
